/**
 * Profile lookup contract — shared between profile-service (producer) and the
 * gateway (consumer). Frozen field names: the gateway and frontend build
 * against these exact shapes.
 *
 * `internal/lookup` returns the full `EntitiesList<Profile>` candidate card, not
 * a reduced lookup row — vacancy membership arrives inside each profile's
 * `applies`, resolved by profile-service when `vacancyId` is passed. No layer
 * produces a `vacancyStatus` field. Only the typeahead and exists projections
 * are declared here.
 */

export type LookupMatchConfidence = 'exact' | 'strong' | 'possible';

/** How a query matched a profile (used for typeahead hints + dedup `via`). */
export type LookupMatchVia =
  | 'email'
  | 'phone'
  | 'telegram'
  | 'link'
  | 'name'
  | 'name-translit'
  | 'notes';

export interface ProfileLookupFilters {
  /** Every selected competence must be present on the profile (AND). */
  competenceIds?: number[];
  hasEmail?: boolean;
  hasPhone?: boolean;
  wasHired?: boolean;
  duplicates?: boolean;
  stageIds?: number[];
  addedByIds?: number[];
  sourceIds?: number[];
}

export interface TypeaheadItem {
  id: number;
  name: string;
  source: string | null;
  avatarUrl?: string | null;
  /** Short hint: a phone, an email or "in notes". */
  matchHint: string;
}

export interface TypeaheadResponse {
  items: TypeaheadItem[];
}

export interface ProfileExistsQuery {
  firstName: string;
  lastName: string;
  middleName?: string;
  emails?: string[];
  phones?: string[];
  links?: string[];
}

export interface ProfileExistsMatch {
  id: number;
  name: string;
  confidence: LookupMatchConfidence;
  via: LookupMatchVia;
}

export interface ProfileExistsResponse {
  matches: ProfileExistsMatch[];
}
